import { createStyles, makeStyles } from "@material-ui/core";
import { Form } from "formik";
import * as React from "react";

import {
    CandidateInterviewFormData,
    CandidateInterviewResult,
    startTimeOptions,
} from ".";
import {
    DateField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Label,
    RelodeApiError,
    RelodeTheme,
    SelectableButtonItem,
    SelectableButtonsList,
    SelectField,
    SwitchField,
    Text,
    TextArea,
    useNotifications,
    useRelodeFormik,
} from "../../";
import * as domain from "../../domain";
import {
    CandidateAddEditInterviewDialogProps,
    InterviewModalState,
    InterviewModalStatus,
} from "./candidateInterviewProps";

const styles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                minWidth: "80%",
                [theme.breakpoints.up("md")]: {
                    minWidth: "400px",
                },
            },
            dialogTitle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            },
            field: {
                margin: theme.spacing(0),
            },
            form: {
                display: "flex",
                flexGrow: 1,
                flexDirection: "column",
            },
            formField: {
                margin: 0,
                // "&:not(:first-child)": {
                //     marginLeft: theme.spacing(3),
                // },
            },
            dateAndTimeSection: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: theme.spacing(3),
                flexGrow: 1,
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(2),
            },
            section: {
                display: "flex",
                flexGrow: 1,
                flexDirection: "column",
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(2),
            },
            button: {
                marginBottom: theme.spacing(2),
            },
            selectableButtonsList: {
                paddingTop: "6px",
                paddingBottom: "7px",
            },
            errorArea: {
                padding: theme.spacing(0, 3),
                margin: theme.spacing(2),
            },
        }),
    { name: "InterviewCandidateModal" }
);

export const CandidateInterview = (props: CandidateAddEditInterviewDialogProps) => {
    const { candidateInterview, onCancel, onSubmit, onComplete } = props;
    const classes = styles({});
    const { actions: notifications } = useNotifications();

    const {
        setFieldTouched,
        formikProps,
        setFieldValue,
        values,
    } = useRelodeFormik<CandidateInterviewFormData>();
    const { errors, isValid, dirty, setFieldError } = formikProps;

    const {
        timeZoneId,
        durationHours,
        durationMinutes,
        interviewStatusTypeId,
        interviewTypeId,
        interviewDetails,
        sendNotification,
    } = values;

    const [state, setState] = React.useState<InterviewModalState>({
        error: undefined,
        status: "unset",
        isBusy: false,
    });

    React.useEffect(() => {
        switch (state.status) {
            // NOTE: DO NOT SET ANY STATE HERE. WILL CAUSE LOOP
            case "pending": {
                notifications.closeAreaNotification("interviewErrorArea");
                break;
            }

            case "failure": {
                if (!state.error) return;

                state.error.validationErrors.forEach(err => {
                    const message = err.messages.join(" ");
                    setFieldError(
                        err.field as keyof CandidateInterviewFormData,
                        message
                    );
                    setFieldTouched(
                        err.field as keyof CandidateInterviewFormData
                    );
                });
                if (state.error.summary) {
                    notifications.showArea(
                        "error",
                        state.error.summary,
                        "interviewErrorArea",
                        {
                            canClose: false,
                        }
                    );
                }
                break;
            }

            case "success": {
                notifications.closeAreaNotification("interviewErrorArea");
                onComplete();
                break;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const setInterviewStatus = (
        status: InterviewModalStatus,
        error: RelodeApiError | undefined
    ) => {
        setState(o => ({ ...o, status, error, isBusy: status === "pending" }));
    };

    const handleOnClose = () => {
        notifications.closeAreaNotification("interviewErrorArea");
        onCancel();
    };

    const handleButtonFieldChange = (field: string) => (
        item: SelectableButtonItem
    ) => {
        setFieldValue(item.id, field as keyof CandidateInterviewFormData);
        setFieldTouched(field as keyof CandidateInterviewFormData);
    };

    const handleTextFieldChange = (event: React.ChangeEvent<any>) => {
        const fieldValue = event.target.value.length > 0 ? event.target.value : null;
        setFieldValue(
            fieldValue,
            "interviewDetails" as keyof CandidateInterviewFormData
        );
        setFieldTouched("interviewDetails" as keyof CandidateInterviewFormData);
    };

    const handleSwitchFieldChange = (event: React.ChangeEvent<any>) => {
        setFieldValue(
            event.target.checked,
            "sendNotification" as keyof CandidateInterviewFormData
        );
        setFieldTouched(
            "sendNotification" as keyof CandidateInterviewFormData
        );
    };

    const handleSubmit = () => {
        setInterviewStatus("pending", undefined);
        onSubmit(CandidateInterviewResult.create(values), setInterviewStatus);
    };

    return (
        <Dialog data-cy="interview" open onClose={handleOnClose}>
            <DialogTitle title="interview details" onClose={handleOnClose} />
            <DialogContent
                isLoading={state.isBusy}
                notificationAreaName="interviewErrorArea"
            >
                <Form className={classes.form}>
                    <Text variant="h4" gutterBottom>
                        {candidateInterview.candidateFullName}
                    </Text>
                    <div className={classes.form}>
                        <div className={classes.dateAndTimeSection}>
                            <DateField
                                label="start date"
                                name="interviewDate"
                                data-cy="interviewDate"
                            />
                            <SelectField
                                mode="list"
                                className={classes.formField}
                                label="start time"
                                id="interviewTime"
                                name="interviewTime"
                                getOptionLabel={option => option.label}
                                setFieldValue={(option: {
                                    label: string;
                                    value: any;
                                }) => option.value}
                                getFieldOption={(o, v) => o.value === v}
                                options={startTimeOptions}
                                data-cy="interviewTime"
                            />
                        </div>
                        <div className={classes.section}>
                            <Label label="time zone" fullWidth>
                                <SelectableButtonsList
                                    id="timeZone"
                                    size="sm"
                                    className={classes.selectableButtonsList}
                                    onClick={handleButtonFieldChange(
                                        "timeZoneId"
                                    )}
                                    items={domain.TimeZone.asArray().map(
                                        timeZone => ({
                                            id: timeZone.id,
                                            label: timeZone.description.split(
                                                " "
                                            )[0],
                                            selected:
                                                timeZoneId === timeZone.id,
                                        })
                                    )}
                                    error={!!errors["timeZoneId"]}
                                    helperText={errors["timeZoneId"]}
                                    data-cy="timeZoneId"
                                />
                            </Label>
                        </div>
                        <div className={classes.section}>
                            <Label label="duration (hours)" fullWidth>
                                <SelectableButtonsList
                                    id="durationHours"
                                    size="sm"
                                    className={classes.selectableButtonsList}
                                    onClick={handleButtonFieldChange(
                                        "durationHours"
                                    )}
                                    items={[0, 1, 2, 3, 4, 5, 6, 7].map(hr => ({
                                        id: hr,
                                        label: hr.toString(),
                                        selected: hr === durationHours,
                                    }))}
                                    error={!!errors["durationHours"]}
                                    helperText={errors["durationHours"]}
                                    data-cy="durationHours"
                                />
                            </Label>
                        </div>
                        <div className={classes.section}>
                            <Label label="duration (minutes)" fullWidth>
                                <SelectableButtonsList
                                    id="durationMinutes"
                                    size="sm"
                                    className={classes.selectableButtonsList}
                                    onClick={handleButtonFieldChange(
                                        "durationMinutes"
                                    )}
                                    items={[0, 15, 30, 45].map(minute => ({
                                        id: minute,
                                        label: minute.toString(),
                                        selected: minute === durationMinutes,
                                    }))}
                                    error={!!errors["durationMinutes"]}
                                    helperText={errors["durationMinutes"]}
                                    data-cy="durationMinutes"
                                />
                            </Label>
                        </div>

                        <div className={classes.section}>
                            <Label label="interview status" fullWidth>
                                <SelectableButtonsList
                                    id="interviewStatus"
                                    className={classes.selectableButtonsList}
                                    onClick={handleButtonFieldChange(
                                        "interviewStatusTypeId"
                                    )}
                                    items={domain.InterviewStatusEnum.asArray().map(
                                        status => ({
                                            id: status.id,
                                            label: status.label,
                                            selected:
                                                status.id ===
                                                interviewStatusTypeId,
                                        })
                                    )}
                                    error={!!errors["interviewStatusTypeId"]}
                                    helperText={errors["interviewStatusTypeId"]}
                                    data-cy="interviewStatus"
                                />
                            </Label>
                        </div>
                        <div className={classes.section}>
                            <Label label="interview type" fullWidth>
                                <SelectableButtonsList
                                    id="interviewType"
                                    className={classes.selectableButtonsList}
                                    onClick={handleButtonFieldChange(
                                        "interviewTypeId"
                                    )}
                                    items={domain.InterviewOptionsEnum.asArray().map(
                                        status => ({
                                            id: status.id,
                                            label: status.label,
                                            selected:
                                                status.id === interviewTypeId,
                                        })
                                    )}
                                    error={!!errors["interviewTypeId"]}
                                    helperText={errors["interviewTypeId"]}
                                    data-cy="interviewType"
                                />
                            </Label>
                        </div>
                        <TextArea
                            id="interviewDetails"
                            className={classes.field}
                            inputProps={{ maxLength: 1500 }}
                            label="interview details"
                            name="interviewDetails"
                            showCharacterLimit
                            multiline={true}
                            rows={5}
                            value={interviewDetails}
                            placeholder="add important interview notes like video links, phone numbers, etc."
                            onChange={handleTextFieldChange}
                        />
                        {!(
                            interviewStatusTypeId ===
                                domain.InterviewStatusEnum.COMPLETED.id ||
                            interviewStatusTypeId ===
                                domain.InterviewStatusEnum.CANCELED.id
                        ) ? (
                            <SwitchField
                                id="sendNotification"
                                name="sendNotification"
                                label="send interview reminders"
                                labelPlacement="end"
                                checked={sendNotification}
                                onSwitchChange={handleSwitchFieldChange}
                            />
                        ) : null}
                    </div>
                </Form>
            </DialogContent>
            <DialogActions
                submit={{
                    label: "submit",
                    onSubmit: handleSubmit,
                    disabled: !isValid || !dirty || state.isBusy,
                }}
                data-cy="saveInterviewButton"
            />
        </Dialog>
    );
};
